import React, { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { LogIn, LogOut, Shield, Crown, User as UserIcon, ChevronDown, Trophy, Sparkles } from 'lucide-react';
import { UserProfile, getExpProgress } from '../../core/profile';

interface Props {
  userProfile: UserProfile;
  onOpenProfileModal: () => void;
  onUpdateNickname?: (name: string) => void;
}

export const AuthUserNav: React.FC<Props> = ({ userProfile, onOpenProfileModal, onUpdateNickname }) => {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout } = useAuth0();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const role = (user?.['https://weiai.ai/role'] as string) || 'user';
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin' || isOwner;

  const expProgress = getExpProgress(userProfile.exp);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync Auth0 profile name into game if user just logged in
  useEffect(() => {
    if (isAuthenticated && user?.nickname && onUpdateNickname) {
      if (userProfile.nickname === '掼蛋大师' || userProfile.nickname === 'Player') {
        onUpdateNickname(user.nickname);
      }
    }
  }, [isAuthenticated, user?.nickname]);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-1.5 bg-slate-900/60 border border-slate-800 rounded-xl px-2 py-1 animate-pulse">
        <div className="w-5 h-5 rounded-lg bg-slate-800" />
        <div className="w-12 h-3 bg-slate-800 rounded hidden sm:block" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="flex items-center space-x-1 sm:space-x-1.5">
        {/* Guest Rank Badge */}
        <button
          onClick={onOpenProfileModal}
          className="bg-gradient-to-r from-amber-500/20 via-amber-400/15 to-sky-500/20 hover:from-amber-500/30 border border-amber-500/40 rounded-xl px-2 py-1 flex items-center space-x-1.5 transition-transform active:scale-95 shadow"
          title="查看段位荣耀与战绩"
        >
          <div className="w-5 h-5 bg-gradient-to-tr from-amber-400 to-amber-600 rounded-lg flex items-center justify-center text-slate-950 font-black text-[10px] shadow">
            {expProgress.tier.icon}
          </div>
          <div className="flex flex-col items-start leading-none text-left">
            <span className="text-[10px] font-black text-amber-300">{expProgress.tier.name}</span>
            <span className="text-[8px] text-slate-400 font-mono scale-90 -ml-0.5">
              Lv.{userProfile.level}
            </span>
          </div>
        </button>

        {/* Login Button */}
        <button
          onClick={() => loginWithRedirect()}
          className="bg-gradient-to-r from-amber-500/20 to-sky-500/20 hover:from-amber-500/30 hover:to-sky-500/30 border border-sky-400/50 text-sky-300 hover:text-white rounded-xl px-2 py-1 flex items-center space-x-1 text-[10px] font-bold transition-all shadow active:scale-95"
          title="通过 Google、微信或邮箱登录，云同步段位与战绩"
        >
          <LogIn className="w-3 h-3 text-sky-400 shrink-0" />
          <span className="whitespace-nowrap">登录</span>
        </button>
      </div>
    );
  }

  const displayName = user.nickname || user.name || userProfile.nickname || '牌友';
  const avatarUrl = user.picture;

  return (
    <div className="relative shrink-0" ref={menuRef}>
      <button
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="bg-slate-900/90 hover:bg-slate-800 border border-slate-700/80 hover:border-amber-400/50 rounded-xl px-2 py-1 flex items-center space-x-1.5 transition-all shadow text-left"
      >
        {/* Avatar or Tier icon */}
        <div className="relative">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="w-5 h-5 rounded-full object-cover border border-amber-400/60 shadow" />
          ) : (
            <div className="w-5 h-5 bg-gradient-to-tr from-amber-400 to-amber-600 rounded-lg flex items-center justify-center text-slate-950 font-black text-[10px] shadow">
              {expProgress.tier.icon}
            </div>
          )}
          {isOwner && (
            <span className="absolute -top-1 -right-1 text-[9px]">👑</span>
          )}
        </div>

        {/* User Info */}
        <div className="flex flex-col items-start leading-none">
          <div className="flex items-center gap-1">
            <span className="text-[10px] font-black text-amber-300 max-w-[80px] truncate">
              {displayName}
            </span>
            {isOwner ? (
              <span className="text-[7px] font-black text-amber-300 bg-amber-400/20 border border-amber-400/40 px-1 py-0.1 rounded uppercase">
                Owner
              </span>
            ) : isAdmin ? (
              <span className="text-[7px] font-bold text-purple-300 bg-purple-400/20 border border-purple-400/40 px-1 py-0.1 rounded uppercase">
                Admin
              </span>
            ) : (
              <span className="text-[7px] font-mono text-sky-300 bg-sky-400/10 px-1 py-0.1 rounded">
                Lv.{userProfile.level}
              </span>
            )}
          </div>
          <span className="text-[8px] text-slate-400 font-mono scale-90 -ml-0.5">
            {expProgress.tier.name}
          </span>
        </div>

        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180 text-amber-400' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {dropdownOpen && (
        <div className="absolute right-0 mt-1.5 w-56 rounded-2xl bg-slate-950/95 backdrop-blur-2xl border border-amber-500/30 shadow-2xl shadow-black/80 py-2.5 px-3 z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Header */}
          <div className="pb-2 mb-2 border-b border-slate-800">
            <div className="flex items-center gap-2">
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-8 h-8 rounded-full border border-amber-400/50" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-amber-900 text-amber-300 flex items-center justify-center font-bold text-xs">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-bold text-slate-100 truncate">{displayName}</span>
                <span className="text-[9px] text-slate-400 truncate">{user.email || '已连接'}</span>
              </div>
            </div>

            <div className="mt-2 flex items-center justify-between text-[10px] bg-slate-900 px-2 py-1 rounded-lg border border-slate-800">
              <span className="text-slate-400">权限级别:</span>
              <span className="font-bold flex items-center gap-1">
                {isOwner ? (
                  <span className="text-amber-400 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> 最高所有者 (Owner)
                  </span>
                ) : isAdmin ? (
                  <span className="text-purple-400 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> 平台管理员 (Admin)
                  </span>
                ) : (
                  <span className="text-sky-400 flex items-center gap-1">
                    <UserIcon className="w-3 h-3" /> 注册牌友 (User)
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Action to view rank details */}
          <button
            type="button"
            onClick={() => {
              setDropdownOpen(false);
              onOpenProfileModal();
            }}
            className="w-full mb-2 flex items-center justify-between px-2.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-[11px] font-semibold text-amber-300 border border-slate-800 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5 text-amber-400" /> 段位荣耀与战绩雷达
            </span>
            <span className="text-[9px] text-slate-400 font-mono">EXP {expProgress.percent}%</span>
          </button>

          {/* Ecosystem Navigation Links */}
          <div className="space-y-1 mb-2">
            <a
              href="https://account.weiai.ai"
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-between px-2 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-[11px] font-semibold text-cyan-300 border border-slate-800 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <UserIcon className="w-3.5 h-3.5 text-cyan-400" /> 用户个人中心
              </span>
              <span className="text-[9px] text-slate-500 font-mono">account ↗</span>
            </a>

            <a
              href="https://music.weiai.ai"
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center justify-between px-2 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-[11px] font-semibold text-cyan-300 border border-slate-800 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <span>🎵</span> AI 音乐学苑
              </span>
              <span className="text-[9px] text-slate-500 font-mono">music ↗</span>
            </a>

            {isAdmin && (
              <a
                href="https://admin.weiai.ai"
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-between px-2 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-[11px] font-semibold text-amber-300 border border-amber-500/30 transition-colors"
              >
                <span className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-amber-400" /> 管理控制台
                </span>
                <span className="text-[9px] text-amber-400 font-mono">admin ↗</span>
              </a>
            )}
          </div>

          {/* Logout Button */}
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 text-xs font-bold transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>退出登录</span>
          </button>
        </div>
      )}
    </div>
  );
};
